/**
 * FTAEditor - Fault Tree Analysis Editor
 * 
 * Structured editor for creating and editing FTA documents per ARP4761A.
 * Features:
 * - Visual fault tree diagram (Mermaid)
 * - Event and gate definition forms
 * - Probability calculation
 * - Minimal cut set extraction
 * - Traceability to reliability data
 * 
 * COMPLIANCE: All basic events must trace to approved reliability data sources.
 */

import { useState, useMemo } from 'react';
import { Button, Input, Label, Textarea, Select } from '@/components/ui';
import type {
  FTADocument,
  FTAEvent,
  FTAGate,
  FTAGateType,
  MinimalCutSet,
} from '@aerotage/shared/safety';
import type { FailureRateUnit } from '@aerotage/shared/reliability';

const GATE_TYPES: FTAGateType[] = ['OR', 'AND', 'PRIORITY_AND', 'INHIBIT', 'XOR', 'VOTE'];
const EVENT_TYPES: FTAEvent['eventType'][] = ['basic', 'intermediate', 'top', 'undeveloped', 'conditioning', 'house'];

interface FTAEditorProps {
  document?: FTADocument;
  projectId: string;
  readOnly?: boolean;
  onSave: (document: FTADocument) => Promise<void>;
  onCancel: () => void;
  onGenerateDiagram?: (document: FTADocument) => Promise<string>; // Returns SVG URL
}

type ActiveTab = 'diagram' | 'events' | 'gates' | 'cut-sets' | 'results';

export function FTAEditor({
  document,
  projectId: _projectId,
  readOnly = false,
  onSave,
  onCancel,
  onGenerateDiagram,
}: FTAEditorProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('diagram');
  const [topEvent, setTopEvent] = useState<FTAEvent>(
    document?.safetyData?.topEvent || {
      id: 'TE-001',
      eventType: 'top',
      description: '',
    }
  );
  const [events, setEvents] = useState<FTAEvent[]>(document?.safetyData?.events || []);
  const [gates, setGates] = useState<FTAGate[]>(document?.safetyData?.gates || []);
  const [minimalCutSets, _setMinimalCutSets] = useState<MinimalCutSet[]>(
    document?.safetyData?.minimalCutSets || []
  );
  const [diagramSvgUrl, setDiagramSvgUrl] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isGeneratingDiagram, setIsGeneratingDiagram] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedGateId, setSelectedGateId] = useState<string | null>(null);

  // All events including top event
  const allEvents = useMemo(() => [topEvent, ...events], [topEvent, events]);

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const updatedDoc: FTADocument = {
        ...document!,
        safetyData: {
          topEvent,
          events,
          gates,
          minimalCutSets,
          calculationResults: document?.safetyData?.calculationResults,
        },
      };
      await onSave(updatedDoc);
    } catch (err) {
      setError(`Failed to save: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Generate diagram
  const handleGenerateDiagram = async () => {
    if (!onGenerateDiagram) return;
    
    setIsGeneratingDiagram(true);
    setError(null);
    try {
      const currentDoc: FTADocument = {
        ...document!,
        safetyData: { topEvent, events, gates, minimalCutSets },
      };
      const svgUrl = await onGenerateDiagram(currentDoc);
      setDiagramSvgUrl(svgUrl);
    } catch (err) {
      setError(`Failed to generate diagram: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsGeneratingDiagram(false);
    }
  };

  // Generate next ID
  const generateId = (prefix: string, items: { id: string }[]) => {
    const allItems = [...allEvents, ...gates, ...items];
    const maxNum = allItems.reduce((max, item) => {
      const match = item.id.match(new RegExp(`^${prefix}-(\\d+)$`));
      if (match) {
        const num = parseInt(match[1], 10);
        return num > max ? num : max;
      }
      return max;
    }, 0);
    return `${prefix}-${String(maxNum + 1).padStart(3, '0')}`;
  };

  // Add new event
  const addEvent = (type: FTAEvent['eventType'] = 'basic') => {
    const prefix = type === 'basic' ? 'BE' : type === 'intermediate' ? 'IE' : 'UE';
    const newEvent: FTAEvent = {
      id: generateId(prefix, events),
      eventType: type,
      description: '',
    };
    setEvents([...events, newEvent]);
    setSelectedEventId(newEvent.id);
  };

  // Update event
  const updateEvent = (id: string, updates: Partial<FTAEvent>) => {
    if (id === topEvent.id) {
      setTopEvent({ ...topEvent, ...updates });
    } else {
      setEvents(events.map(e => e.id === id ? { ...e, ...updates } : e));
    }
  };

  // Delete event
  const deleteEvent = (id: string) => {
    if (id === topEvent.id) return; // Can't delete top event
    setEvents(events.filter(e => e.id !== id));
    // Remove from gate inputs
    setGates(gates.map(g => ({
      ...g,
      inputEventIds: g.inputEventIds.filter(eid => eid !== id),
    })));
    if (selectedEventId === id) setSelectedEventId(null);
  };

  // Add new gate
  const addGate = (type: FTAGateType = 'OR') => {
    const newGate: FTAGate = {
      id: generateId('G', gates),
      gateType: type,
      outputEventId: '',
      inputEventIds: [],
      inputGateIds: [],
    };
    setGates([...gates, newGate]);
    setSelectedGateId(newGate.id);
  };

  // Update gate
  const updateGate = (id: string, updates: Partial<FTAGate>) => {
    setGates(gates.map(g => g.id === id ? { ...g, ...updates } : g));
  };

  // Delete gate
  const deleteGate = (id: string) => {
    setGates(gates.filter(g => g.id !== id));
    if (selectedGateId === id) setSelectedGateId(null);
  };

  // Calculate probability (simplified)
  const calculateProbability = () => {
    // This would call the calculation engine
    // For now, show a placeholder message
    setError('Probability calculation requires the aerotage_calc_engine backend. Use the AI assistant to perform calculations.');
  };

  // Extract minimal cut sets (simplified)
  const extractMinimalCutSets = () => {
    // This would call the calculation engine
    setError('MCS extraction requires the aerotage_calc_engine backend. Use the AI assistant to extract cut sets.');
  };

  const _selectedEvent = selectedEventId ? allEvents.find(e => e.id === selectedEventId) : null;
  const _selectedGate = selectedGateId ? gates.find(g => g.id === selectedGateId) : null;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-border">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            FTA Editor
          </h2>
          <p className="text-sm text-muted-foreground">
            {document?.metadata?.failureConditionDescription || 'Fault Tree Analysis'}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={readOnly || isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-warning-light border border-warning rounded-lg text-warning-foreground text-sm">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex-shrink-0 px-4 border-b border-border">
        <div className="flex gap-4">
          {(['diagram', 'events', 'gates', 'cut-sets', 'results'] as ActiveTab[]).map(tab => (
            <TabButton
              key={tab}
              active={activeTab === tab}
              onClick={() => setActiveTab(tab)}
              label={tab.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            />
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {activeTab === 'diagram' && (
          <DiagramPanel
            svgUrl={diagramSvgUrl}
            onGenerate={handleGenerateDiagram}
            isGenerating={isGeneratingDiagram}
            topEvent={topEvent}
            events={events}
            gates={gates}
          />
        )}
        {activeTab === 'events' && (
          <EventsPanel
            topEvent={topEvent}
            events={events}
            selectedId={selectedEventId}
            onSelect={setSelectedEventId}
            onAdd={addEvent}
            onUpdate={updateEvent}
            onDelete={deleteEvent}
            readOnly={readOnly}
          />
        )}
        {activeTab === 'gates' && (
          <GatesPanel
            gates={gates}
            allEvents={allEvents}
            selectedId={selectedGateId}
            onSelect={setSelectedGateId}
            onAdd={addGate}
            onUpdate={updateGate}
            onDelete={deleteGate}
            readOnly={readOnly}
          />
        )}
        {activeTab === 'cut-sets' && (
          <CutSetsPanel
            cutSets={minimalCutSets}
            events={allEvents}
            onExtract={extractMinimalCutSets}
          />
        )}
        {activeTab === 'results' && (
          <ResultsPanel
            document={document}
            topEvent={topEvent}
            cutSets={minimalCutSets}
            onCalculate={calculateProbability}
          />
        )}
      </div>
    </div>
  );
}

// Tab button
interface TabButtonProps {
  active: boolean;
  onClick: () => void;
  label: string;
}

function TabButton({ active, onClick, label }: TabButtonProps) {
  return (
    <Button
      onClick={onClick}
      variant="ghost"
      className={`py-2 px-3 text-sm font-medium border-b-2 rounded-none h-auto ${
        active
          ? 'border-primary text-info'
          : 'border-transparent text-muted-foreground hover:text-foreground'
      }`}
    >
      {label}
    </Button>
  );
}

// Diagram Panel
interface DiagramPanelProps {
  svgUrl: string | null;
  onGenerate: () => void;
  isGenerating: boolean;
  topEvent: FTAEvent;
  events: FTAEvent[];
  gates: FTAGate[];
}

function DiagramPanel({ svgUrl, onGenerate, isGenerating, topEvent, events, gates }: DiagramPanelProps) {
  const eventCount = events.length + 1; // +1 for top event
  const gateCount = gates.length;
  
  // Generate simple Mermaid text preview
  const mermaidPreview = useMemo(() => {
    const lines = ['flowchart TD'];
    
    // Add top event
    lines.push(`  ${topEvent.id}["${topEvent.id}: ${topEvent.description || 'Top Event'}"]`);
    
    // Add gates and their connections
    gates.forEach(gate => {
      const gateSymbol = gate.gateType === 'OR' ? '((OR))' : 
                         gate.gateType === 'AND' ? '{{AND}}' : 
                         `[${gate.gateType}]`;
      lines.push(`  ${gate.id}${gateSymbol}`);
      
      if (gate.outputEventId) {
        lines.push(`  ${gate.id} --> ${gate.outputEventId}`);
      }
      
      gate.inputEventIds.forEach(eid => {
        lines.push(`  ${eid} --> ${gate.id}`);
      });
    });
    
    // Add events
    events.forEach(event => {
      const _shape = event.eventType === 'basic' ? '((' + event.id + '))' :
                   event.eventType === 'undeveloped' ? '>' + event.id + ']' :
                   '[' + event.id + ']';
      lines.push(`  ${event.id}["${event.description || event.id}"]`);
    });
    
    return lines.join('\n');
  }, [topEvent, events, gates]);

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground">Fault Tree Diagram</h3>
        <Button onClick={onGenerate} disabled={isGenerating}>
          {isGenerating ? 'Generating...' : '🔄 Generate Diagram'}
        </Button>
      </div>
      
      {/* Stats */}
      <div className="flex gap-4 mb-4 text-sm">
        <span className="text-muted-foreground">Events: <strong>{eventCount}</strong></span>
        <span className="text-muted-foreground">Gates: <strong>{gateCount}</strong></span>
        <span className="text-muted-foreground">Basic Events: <strong>{events.filter(e => e.eventType === 'basic').length}</strong></span>
      </div>
      
      {svgUrl ? (
        <div className="border border-border rounded-lg p-4 bg-background">
          <img src={svgUrl} alt="Fault Tree Diagram" className="max-w-full h-auto" />
        </div>
      ) : (
        <div className="border border-border rounded-lg p-4 bg-muted/30">
          <h4 className="font-medium text-foreground mb-2">Mermaid Preview</h4>
          <pre className="text-xs text-muted-foreground overflow-auto max-h-96 p-2 bg-background rounded">
            {mermaidPreview}
          </pre>
          <p className="text-sm text-muted-foreground mt-4">
            Click "Generate Diagram" to render the fault tree as an SVG image.
          </p>
        </div>
      )}
    </div>
  );
}

// Events Panel
interface EventsPanelProps {
  topEvent: FTAEvent;
  events: FTAEvent[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onAdd: (type?: FTAEvent['eventType']) => void;
  onUpdate: (id: string, updates: Partial<FTAEvent>) => void;
  onDelete: (id: string) => void;
  readOnly: boolean;
}

function EventsPanel({
  topEvent,
  events,
  selectedId,
  onSelect,
  onAdd,
  onUpdate,
  onDelete,
  readOnly,
}: EventsPanelProps) {
  const allEvents = [topEvent, ...events];
  const selectedEvent = selectedId ? allEvents.find(e => e.id === selectedId) : null;

  const getEventIcon = (type: FTAEvent['eventType']) => {
    switch (type) {
      case 'top': return '🔺';
      case 'intermediate': return '◇';
      case 'basic': return '○';
      case 'undeveloped': return '◇';
      case 'conditioning': return '⬭';
      case 'house': return '⌂';
      default: return '●';
    }
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Event list */}
      <div className="w-1/3 border-r border-border overflow-y-auto">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Events</h3>
            {!readOnly && (
              <Select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    onAdd(e.target.value as FTAEvent['eventType']);
                    e.target.value = '';
                  }
                }}
                className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="">+ Add...</option>
                <option value="basic">Basic Event</option>
                <option value="intermediate">Intermediate Event</option>
                <option value="undeveloped">Undeveloped Event</option>
                <option value="conditioning">Conditioning Event</option>
                <option value="house">House Event</option>
              </Select>
            )}
          </div>
          
          <div className="space-y-2">
            {allEvents.map(event => (
              <div
                key={event.id}
                onClick={() => onSelect(event.id)}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedId === event.id
                    ? 'border-primary bg-info-light'
                    : 'border-border hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center gap-2">
                  <span>{getEventIcon(event.eventType)}</span>
                  <span className="text-xs font-mono text-muted-foreground">{event.id}</span>
                  <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                    {event.eventType}
                  </span>
                </div>
                <div className="text-sm text-foreground mt-1 line-clamp-2">
                  {event.description || '(No description)'}
                </div>
                {event.eventType === 'basic' && event.failureRate && (
                  <div className="text-xs text-muted-foreground mt-1 font-mono">
                    λ = {event.failureRate.toExponential(2)}/{event.failureRateUnit || 'hr'}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Event detail */}
      <div className="flex-1 overflow-y-auto p-4">
        {selectedEvent ? (
          <EventDetailForm
            event={selectedEvent}
            isTopEvent={selectedEvent.id === topEvent.id}
            onUpdate={(updates) => onUpdate(selectedEvent.id, updates)}
            onDelete={() => onDelete(selectedEvent.id)}
            readOnly={readOnly}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p>Select an event to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Event detail form
interface EventDetailFormProps {
  event: FTAEvent;
  isTopEvent: boolean;
  onUpdate: (updates: Partial<FTAEvent>) => void;
  onDelete: () => void;
  readOnly: boolean;
}

function EventDetailForm({
  event,
  isTopEvent,
  onUpdate,
  onDelete,
  readOnly,
}: EventDetailFormProps) {
  const showFailureRate = event.eventType === 'basic';
  const showHouseState = event.eventType === 'house';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">{event.id}</h3>
        {!readOnly && !isTopEvent && (
          <Button variant="secondary" size="sm" onClick={onDelete}>
            Delete
          </Button>
        )}
      </div>

      <div>
        <Label>Event Type</Label>
        <Select
          value={event.eventType}
          onChange={(e) => onUpdate({ eventType: e.target.value as FTAEvent['eventType'] })}
          disabled={readOnly || isTopEvent}
          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          {EVENT_TYPES.map(t => (
            <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
          ))}
        </Select>
      </div>

      <div>
        <Label>Description *</Label>
        <Textarea
          value={event.description}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder={isTopEvent ? "Top event description (failure condition)" : "Event description..."}
          rows={2}
          disabled={readOnly}
        />
      </div>

      {showFailureRate && (
        <div className="p-4 border border-border rounded-lg bg-muted/30">
          <h4 className="font-medium text-foreground mb-3">Failure Rate Data</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Failure Rate (λ)</Label>
              <Input
                type="number"
                value={event.failureRate || ''}
                onChange={(e) => onUpdate({ failureRate: parseFloat(e.target.value) || undefined })}
                placeholder="e.g., 1.5e-6"
                disabled={readOnly}
              />
            </div>
            <div>
              <Label>Unit</Label>
              <Select
                value={event.failureRateUnit || 'per_hour'}
                onChange={(e) => onUpdate({ failureRateUnit: e.target.value as FailureRateUnit })}
                disabled={readOnly}
                className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="per_hour">per hour (/hr)</option>
                <option value="per_million_hours">per million hours</option>
                <option value="FIT">FIT (failures in 10⁹ hrs)</option>
              </Select>
            </div>
          </div>

          <div className="mt-3">
            <Label>Exposure Time (hours)</Label>
            <Input
              type="number"
              value={event.exposureTime || ''}
              onChange={(e) => onUpdate({ exposureTime: parseFloat(e.target.value) || undefined })}
              placeholder="e.g., 1.0 (per flight hour)"
              disabled={readOnly}
            />
          </div>

          {event.probability !== undefined && (
            <div className="mt-3 p-2 bg-info-light rounded">
              <span className="text-sm">Calculated Probability: <strong>{event.probability.toExponential(2)}</strong></span>
            </div>
          )}

          <div className="mt-3">
            <Label className="flex items-center gap-2">
              {/* Checkbox: no Checkbox UI component available */}
              {/* eslint-disable-next-line no-restricted-syntax */}
              <input
                type="checkbox"
                checked={event.isLatent || false}
                onChange={(e) => onUpdate({ isLatent: e.target.checked })}
                disabled={readOnly}
              />
              Latent Failure
            </Label>
            {event.isLatent && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Latency Period (hrs)</Label>
                  <Input
                    type="number"
                    value={event.latencyPeriod || ''}
                    onChange={(e) => onUpdate({ latencyPeriod: parseFloat(e.target.value) || undefined })}
                    disabled={readOnly}
                    className="h-8"
                  />
                </div>
                <div>
                  <Label className="text-xs">Detection Method</Label>
                  <Input
                    value={event.detectionMethod || ''}
                    onChange={(e) => onUpdate({ detectionMethod: e.target.value })}
                    disabled={readOnly}
                    className="h-8"
                  />
                </div>
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground mt-3">
            ⚠️ Basic events MUST trace to approved reliability data sources for certification.
          </p>
        </div>
      )}

      {showHouseState && (
        <div>
          <Label className="flex items-center gap-2">
            {/* Checkbox: no Checkbox UI component available */}
            {/* eslint-disable-next-line no-restricted-syntax */}
            <input
              type="checkbox"
              checked={event.houseEventState || false}
              onChange={(e) => onUpdate({ houseEventState: e.target.checked })}
              disabled={readOnly}
            />
            House Event State (TRUE = occurs, FALSE = doesn't occur)
          </Label>
        </div>
      )}

      <div>
        <Label>Notes</Label>
        <Textarea
          value={event.notes || ''}
          onChange={(e) => onUpdate({ notes: e.target.value })}
          placeholder="Any additional notes..."
          rows={2}
          disabled={readOnly}
        />
      </div>
    </div>
  );
}

// Gates Panel
interface GatesPanelProps {
  gates: FTAGate[];
  allEvents: FTAEvent[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onAdd: (type?: FTAGateType) => void;
  onUpdate: (id: string, updates: Partial<FTAGate>) => void;
  onDelete: (id: string) => void;
  readOnly: boolean;
}

function GatesPanel({
  gates,
  allEvents,
  selectedId,
  onSelect,
  onAdd,
  onUpdate,
  onDelete,
  readOnly,
}: GatesPanelProps) {
  const selectedGate = selectedId ? gates.find(g => g.id === selectedId) : null;

  const getGateSymbol = (type: FTAGateType) => {
    switch (type) {
      case 'OR': return '∨';
      case 'AND': return '∧';
      case 'PRIORITY_AND': return '∧⃗';
      case 'INHIBIT': return '⊲';
      case 'XOR': return '⊕';
      case 'VOTE': return 'k/n';
      default: return '?';
    }
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Gate list */}
      <div className="w-1/3 border-r border-border overflow-y-auto">
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Gates</h3>
            {!readOnly && (
              <Select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    onAdd(e.target.value as FTAGateType);
                    e.target.value = '';
                  }
                }}
                className="h-8 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="">+ Add...</option>
                {GATE_TYPES.map(t => (
                  <option key={t} value={t}>{t} Gate</option>
                ))}
              </Select>
            )}
          </div>
          
          {gates.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No gates defined</p>
              <p className="text-sm">Add gates to connect events in the fault tree</p>
            </div>
          ) : (
            <div className="space-y-2">
              {gates.map(gate => {
                const outputEvent = allEvents.find(e => e.id === gate.outputEventId);
                return (
                  <div
                    key={gate.id}
                    onClick={() => onSelect(gate.id)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedId === gate.id
                        ? 'border-primary bg-info-light'
                        : 'border-border hover:bg-muted/50'
                    } ${gate.requiresCMA ? 'border-l-4 border-l-warning' : ''}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-mono">{getGateSymbol(gate.gateType)}</span>
                      <span className="text-xs font-mono text-muted-foreground">{gate.id}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                        {gate.gateType}
                      </span>
                      {gate.requiresCMA && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-warning-light text-warning">
                          CMA
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      → {outputEvent?.description || gate.outputEventId || '(not connected)'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {gate.inputEventIds.length} input(s)
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Gate detail */}
      <div className="flex-1 overflow-y-auto p-4">
        {selectedGate ? (
          <GateDetailForm
            gate={selectedGate}
            allEvents={allEvents}
            gates={gates}
            onUpdate={(updates) => onUpdate(selectedGate.id, updates)}
            onDelete={() => onDelete(selectedGate.id)}
            readOnly={readOnly}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground">
            <p>Select a gate to view details</p>
          </div>
        )}
      </div>
    </div>
  );
}

// Gate detail form
interface GateDetailFormProps {
  gate: FTAGate;
  allEvents: FTAEvent[];
  gates: FTAGate[];
  onUpdate: (updates: Partial<FTAGate>) => void;
  onDelete: () => void;
  readOnly: boolean;
}

function GateDetailForm({
  gate,
  allEvents,
  gates: _gates,
  onUpdate,
  onDelete,
  readOnly,
}: GateDetailFormProps) {
  const showVoteParams = gate.gateType === 'VOTE';
  const showPriorityParams = gate.gateType === 'PRIORITY_AND';
  const showInhibitParams = gate.gateType === 'INHIBIT';
  const showCMAWarning = gate.gateType === 'AND' || gate.gateType === 'PRIORITY_AND';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-foreground">{gate.id}</h3>
        {!readOnly && (
          <Button variant="secondary" size="sm" onClick={onDelete}>
            Delete
          </Button>
        )}
      </div>

      <div>
        <Label>Gate Type</Label>
        <Select
          value={gate.gateType}
          onChange={(e) => onUpdate({ gateType: e.target.value as FTAGateType })}
          disabled={readOnly}
          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          {GATE_TYPES.map(t => (
            <option key={t} value={t}>{t} Gate</option>
          ))}
        </Select>
      </div>

      <div>
        <Label>Description</Label>
        <Input
          value={gate.description || ''}
          onChange={(e) => onUpdate({ description: e.target.value })}
          placeholder="Optional gate description..."
          disabled={readOnly}
        />
      </div>

      <div>
        <Label>Output Event (gate feeds into)</Label>
        <Select
          value={gate.outputEventId}
          onChange={(e) => onUpdate({ outputEventId: e.target.value })}
          disabled={readOnly}
          className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">(Select output event)</option>
          {allEvents.filter(e => e.eventType !== 'basic').map(e => (
            <option key={e.id} value={e.id}>{e.id}: {e.description}</option>
          ))}
        </Select>
      </div>

      <div>
        <Label>Input Events (feed into this gate)</Label>
        <div className="space-y-2 mt-2">
          {gate.inputEventIds.map((eid, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <Select
                value={eid}
                onChange={(e) => {
                  const newInputs = [...gate.inputEventIds];
                  newInputs[idx] = e.target.value;
                  onUpdate({ inputEventIds: newInputs });
                }}
                disabled={readOnly}
                className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-sm"
              >
                <option value="">(Select event)</option>
                {allEvents.map(e => (
                  <option key={e.id} value={e.id}>{e.id}: {e.description}</option>
                ))}
              </Select>
              {!readOnly && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    const newInputs = gate.inputEventIds.filter((_, i) => i !== idx);
                    onUpdate({ inputEventIds: newInputs });
                  }}
                >
                  ×
                </Button>
              )}
            </div>
          ))}
          {!readOnly && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onUpdate({ inputEventIds: [...gate.inputEventIds, ''] })}
            >
              + Add Input
            </Button>
          )}
        </div>
      </div>

      {showVoteParams && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>k (minimum to trigger)</Label>
            <Input
              type="number"
              value={gate.kValue || ''}
              onChange={(e) => onUpdate({ kValue: parseInt(e.target.value) || undefined })}
              placeholder="e.g., 2"
              disabled={readOnly}
            />
          </div>
          <div>
            <Label>n (total inputs)</Label>
            <Input
              type="number"
              value={gate.nValue || gate.inputEventIds.length}
              onChange={(e) => onUpdate({ nValue: parseInt(e.target.value) || undefined })}
              placeholder="e.g., 3"
              disabled={readOnly}
            />
          </div>
        </div>
      )}

      {showPriorityParams && (
        <div>
          <Label>Required Order Factor (ROF)</Label>
          <Input
            type="number"
            value={gate.requiredOrderFactor || ''}
            onChange={(e) => onUpdate({ requiredOrderFactor: parseFloat(e.target.value) || undefined })}
            placeholder="e.g., 0.5"
            disabled={readOnly}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Factor accounting for the probability that failures occur in the required sequence.
          </p>
        </div>
      )}

      {showInhibitParams && (
        <div>
          <Label>Conditioning Event</Label>
          <Select
            value={gate.conditioningEventId || ''}
            onChange={(e) => onUpdate({ conditioningEventId: e.target.value || undefined })}
            disabled={readOnly}
            className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">(Select conditioning event)</option>
            {allEvents.filter(e => e.eventType === 'conditioning').map(e => (
              <option key={e.id} value={e.id}>{e.id}: {e.description}</option>
            ))}
          </Select>
        </div>
      )}

      {showCMAWarning && (
        <div className="p-3 border border-warning bg-warning-light rounded-lg">
          <Label className="flex items-center gap-2">
            {/* Checkbox: no Checkbox UI component available */}
            {/* eslint-disable-next-line no-restricted-syntax */}
            <input
              type="checkbox"
              checked={gate.requiresCMA || false}
              onChange={(e) => onUpdate({ requiresCMA: e.target.checked })}
              disabled={readOnly}
            />
            Requires Common Mode Analysis (CMA)
          </Label>
          <p className="text-xs text-warning-foreground mt-1">
            ⚠️ AND/PRIORITY_AND gates assume independence of inputs. 
            CMA is required to verify this independence per ARP4761A.
          </p>
        </div>
      )}

      <div>
        <Label>Notes</Label>
        <Textarea
          value={gate.notes || ''}
          onChange={(e) => onUpdate({ notes: e.target.value })}
          placeholder="Any additional notes..."
          rows={2}
          disabled={readOnly}
        />
      </div>
    </div>
  );
}

// Cut Sets Panel
interface CutSetsPanelProps {
  cutSets: MinimalCutSet[];
  events: FTAEvent[];
  onExtract: () => void;
}

function CutSetsPanel({ cutSets, events, onExtract }: CutSetsPanelProps) {
  const getEventDescription = (id: string) => {
    const event = events.find(e => e.id === id);
    return event?.description || id;
  };

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground">Minimal Cut Sets</h3>
        <Button onClick={onExtract}>Extract Cut Sets</Button>
      </div>
      
      <p className="text-sm text-muted-foreground mb-4">
        Minimal cut sets are the smallest combinations of basic events that can cause the top event.
      </p>
      
      {cutSets.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p>No cut sets extracted yet</p>
          <p className="text-sm">Click "Extract Cut Sets" after defining the fault tree structure</p>
        </div>
      ) : (
        <div className="space-y-3">
          {cutSets.map((cs, idx) => (
            <div key={cs.id || idx} className="p-4 border border-border rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-sm">{cs.id || `MCS-${idx + 1}`}</span>
                <span className="font-mono text-sm">
                  P = {cs.probability.toExponential(2)}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {cs.eventIds.map(eid => (
                  <span
                    key={eid}
                    className="px-2 py-1 text-xs rounded bg-muted text-foreground"
                    title={getEventDescription(eid)}
                  >
                    {eid}
                  </span>
                ))}
              </div>
              {cs.description && (
                <p className="text-sm text-muted-foreground mt-2">{cs.description}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Results Panel
interface ResultsPanelProps {
  document?: FTADocument;
  topEvent: FTAEvent;
  cutSets: MinimalCutSet[];
  onCalculate: () => void;
}

function ResultsPanel({ document, topEvent: _topEvent, cutSets, onCalculate }: ResultsPanelProps) {
  const results = document?.safetyData?.calculationResults;
  const objective = document?.metadata?.probabilityObjective || 1.0e-9;
  
  const meetsObjective = results?.topEventProbability !== undefined && 
    results.topEventProbability <= objective;

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-foreground">Calculation Results</h3>
        <Button onClick={onCalculate}>Calculate Probability</Button>
      </div>
      
      {/* Objective */}
      <div className="p-4 border border-border rounded-lg mb-4">
        <h4 className="font-medium text-foreground mb-2">Probability Objective</h4>
        <div className="text-2xl font-mono text-foreground">
          {objective.toExponential(0)} /flight hour
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Based on {document?.metadata?.severityClassification || 'Catastrophic'} severity classification
        </p>
      </div>
      
      {results ? (
        <>
          {/* Top Event Probability */}
          <div className={`p-4 border rounded-lg mb-4 ${
            meetsObjective 
              ? 'border-success bg-success-light' 
              : 'border-destructive bg-destructive-light'
          }`}>
            <h4 className="font-medium text-foreground mb-2">Top Event Probability</h4>
            <div className="text-3xl font-mono text-foreground">
              {results.topEventProbability.toExponential(2)} /flight hour
            </div>
            <p className={`text-sm mt-2 ${meetsObjective ? 'text-success' : 'text-destructive'}`}>
              {meetsObjective 
                ? '✓ Meets probability objective' 
                : '✗ Does NOT meet probability objective'}
            </p>
          </div>
          
          {/* Cut Set Contributions */}
          {cutSets.length > 0 && (
            <div className="p-4 border border-border rounded-lg">
              <h4 className="font-medium text-foreground mb-2">Cut Set Contributions</h4>
              <div className="space-y-2">
                {cutSets.slice(0, 5).map((cs, idx) => {
                  const contribution = (cs.probability / (results.topEventProbability || 1)) * 100;
                  return (
                    <div key={cs.id || idx} className="flex items-center gap-2">
                      <span className="text-sm font-mono w-20">{cs.id || `MCS-${idx + 1}`}</span>
                      <div className="flex-1 h-4 bg-muted rounded overflow-hidden">
                        {/* Dynamic width requires inline style */}
                      <div 
                          className="h-full bg-primary" 
                          style={{ width: `${Math.min(contribution, 100)}%` }} // eslint-disable-line no-restricted-syntax
                        />
                      </div>
                      <span className="text-sm text-muted-foreground w-16 text-right">
                        {contribution.toFixed(1)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 text-muted-foreground">
          <p>No calculation results yet</p>
          <p className="text-sm">Click "Calculate Probability" to compute the top event probability</p>
        </div>
      )}
    </div>
  );
}

export default FTAEditor;


